import { useEffect, useMemo, useState } from 'react';

import {
  AssetRepairColumnId,
  BULK_PDF_EXPORT_LIMIT,
  GetRepairsRequestDTO,
  GetRepairsResponseDTO,
  MODULE,
  RepairOrderByFieldEnumSchema,
} from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ColumnFiltersState, RowSelectionState } from '@tanstack/react-table';
import { useDebounce } from '@uidotdev/usehooks';
import { useLocation, useParams } from 'react-router-dom';
import { z } from 'zod';

import { DeleteConfirmationDialog } from '@/components/common/delete-confirmation-dialog';
import DeleteIcon from '@/components/icons/delete';
import DownloadCsvIcon from '@/components/icons/download-csv';
import DownloadPdfIcon from '@/components/icons/download-pdf';
import UndoIcon from '@/components/icons/undo';
import ColumnsDialog from '@/components/table-page/columns-dialog';
import { TablePageHeader, TablePageHeaderActionButton, TablePageTableGroupDropdown } from '@/components/table-page/table-page-header';
import TablePageSearchBar from '@/components/table-page/table-page-search-bar';
import { TablePageSelectedCountIndicator } from '@/components/table-page/table-page-selected-count-indicator';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { useToast } from '@/components/ui/toaster';
import { useInfiniteScrollTrigger } from '@/hooks/use-infinite-scroll-trigger';
import { usePageConfig } from '@/hooks/use-page-config';
import { usePermissions } from '@/hooks/use-permissions';
import { useResetRowSelectionOnChange } from '@/hooks/use-reset-row-selection-on-change';
import { buildFilterFields } from '@/queries/column-filter';
import { getRepairsInfiniteQueryOptions, repairKeys } from '@/queries/repairs';
import { ApiService } from '@/services/api-v2';
import { useModulesStore } from '@/stores/modules.store';
import { openExportTab } from '@/utils/export-tab';
import { buildCsvExportFilename } from '@/utils/export-to-csv';
import { buildPdfExportJob } from '@/utils/register-pdf-export';

import { AssetRepairsTable, NoRepairAvailableDisplay, NoRepairFoundFromFiltersDisplay, NoRepairFoundFromSearchDisplay } from './table';
import { FILTER_FIELD_MAP, groupOptions, columns as repairsColumns } from './table-meta';

const AssetRepairsPage = () => {
  const { id } = useParams();

  const queryClient = useQueryClient();
  const toast = useToast();
  const { state } = useLocation();
  const { canEditRepairRecord, canExportCsv } = usePermissions();

  const { setModuleFilters } = useModulesStore();

  const {
    config: { columnOrder, grouping, sorting, filters, columnVisibility },
    loading: isLoadingPageConfig,
    updatePageConfig,
  } = usePageConfig(MODULE.AssetRepairs, {
    filterFieldMap: FILTER_FIELD_MAP,
    groupOptions,
    columns: repairsColumns,
  });

  const [openColumnsDialog, setOpenColumnsDialog] = useState(false);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const [search, setSearch] = useState('');

  const debouncedSearch = useDebounce(search, 500);

  const {
    data: repairsData,
    fetchNextPage: fetchNextRepairsPage,
    hasNextPage: hasNextRepairsPage,
    isFetching: isFetchingRepairs,
    isFetchingNextPage: isFetchingNextRepairsPage,
    error: repairsError,
  } = useInfiniteQuery(
    getRepairsInfiniteQueryOptions({
      filters: {
        search: debouncedSearch,
        ...buildFilterFields(filters, FILTER_FIELD_MAP, {
          transformValue: (columnId, raw) => (columnId === 'formNumber' ? (raw as string | undefined)?.replace(/[^0-9]/g, '') : raw),
          extraFields: { assetId: [id as string] },
        }),
      },
      groupBy: grouping.length
        ? { field: grouping[0] as NonNullable<GetRepairsRequestDTO['groupBy']>['field'], direction: (sorting.direction || 'asc') as 'asc' | 'desc' }
        : undefined,
      orderBy: sorting.key ? { field: sorting.key as z.infer<typeof RepairOrderByFieldEnumSchema>, direction: sorting.direction as 'asc' | 'desc' } : undefined,
      enabled: !isLoadingPageConfig,
    }),
  );

  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const { sentinelRef, canLoadMore, loadMore } = useInfiniteScrollTrigger({
    fetchNextPage: fetchNextRepairsPage,
    hasNextPage: hasNextRepairsPage,
    isFetching: isFetchingRepairs,
    isError: !!repairsError,
    root: scrollRoot,
  });

  const getSelectedRepairs = useMemo(() => {
    return (incomingData: typeof repairsData) => {
      if (!incomingData?.pages) return [];
      const ids = Object.keys(selectedRows);
      const assets = incomingData.pages.flatMap((page) => page.data);
      return ids.filter((id) => !isNaN(parseInt(id))).map((id) => assets[parseInt(id)]);
    };
  }, [selectedRows]);

  const { mutate: deleteRepairs, isPending: isDeletingRepairs } = useMutation({
    mutationFn: async (ids: string[]) => await ApiService.DeleteRepairs({ repairIds: ids }),
    onSuccess: () => {
      toast.success('Repairs deleted successfully');
      setOpenDeleteConfirmation(false);
      setSelectedRows({});

      queryClient.invalidateQueries({ queryKey: repairKeys.all });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleDeleteRepairs = () => {
    if (repairsData?.pages) {
      const toDelete = getSelectedRepairs(repairsData);
      deleteRepairs(toDelete.map((repair) => repair.id));
    }
  };

  const selectedRepairs = useMemo(() => getSelectedRepairs(repairsData), [repairsData, getSelectedRepairs]);
  const canBulkActOnSelectedRepairs = selectedRepairs.length > 0 && selectedRepairs.every((repair) => canEditRepairRecord(repair.repairedByUserId));

  function handleCsvExport() {
    if (!repairsData?.pages) return;

    const selected = getSelectedRepairs(repairsData);

    const activeColumnIds = columnOrder.filter((column) => column.id !== 'select' && column.active).map((column) => column.id);
    openExportTab({
      kind: 'csv',
      request: {
        queryType: 'repairs',
        filename: buildCsvExportFilename('repairs'),
        filters: { fields: { id: selected.map((repair) => repair.id) } },
        groupBy: grouping.length
          ? { field: grouping[0] as NonNullable<GetRepairsRequestDTO['groupBy']>['field'], direction: (sorting.direction || 'asc') as 'asc' | 'desc' }
          : undefined,
        orderBy: sorting.key
          ? { field: sorting.key as z.infer<typeof RepairOrderByFieldEnumSchema>, direction: sorting.direction as 'asc' | 'desc' }
          : undefined,
        columns: activeColumnIds,
      },
    });
  }

  function handlePdfExport() {
    if (!repairsData?.pages) return;
    const selected = getSelectedRepairs(repairsData);
    if (selected.length > BULK_PDF_EXPORT_LIMIT) {
      toast.error(`You can export up to ${BULK_PDF_EXPORT_LIMIT} records at a time. Deselect some and try again.`);
      return;
    }
    openExportTab(buildPdfExportJob({ scope: 'records', recordType: 'repair', records: selected }));
  }

  useEffect(() => {
    if (repairsError) {
      toast.error(repairsError.message);
    }
  }, [toast, repairsError]);

  useResetRowSelectionOnChange(setSelectedRows, [debouncedSearch, filters, sorting.key, sorting.direction, grouping]);

  const flattenedData = useMemo(() => repairsData?.pages.flatMap((page) => page.data) ?? [], [repairsData]);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <TablePageHeader>
        <div className="no-scrollbar h-full w-full overflow-x-auto py-0.5">
          <div className="flex h-full min-w-[580px] items-center justify-between">
            {!selectedRepairs.length && (
              <>
                <div className="flex h-full items-center gap-x-2 px-2">
                  <TablePageSearchBar search={search} setSearch={setSearch} />
                  {filters && filters.length > 0 && (
                    <TablePageHeaderActionButton
                      icon={<UndoIcon />}
                      label="Remove all Filters"
                      kind="secondary"
                      onClick={() => setModuleFilters(MODULE.AssetRepairs, [])}
                    />
                  )}
                </div>

                <div className="mr-1 flex h-full items-center">
                  <div className="flex items-center">
                    <TablePageTableGroupDropdown
                      columns={groupOptions}
                      value={grouping.length ? grouping[0] : ''}
                      onChange={(value) =>
                        updatePageConfig({
                          table: { grouping: value ? [value] : [] },
                        })
                      }
                    />
                    <ColumnsDialog<GetRepairsResponseDTO['data'][number]>
                      open={openColumnsDialog}
                      onOpenChange={setOpenColumnsDialog}
                      columns={repairsColumns}
                      defaultSectionLabel="Repair"
                      columnOrder={columnOrder}
                      onColumnOrderChange={(columnOrder) =>
                        updatePageConfig({
                          table: { columnOrder: columnOrder as Array<{ id: AssetRepairColumnId; active: boolean }> },
                        })
                      }
                    />
                  </div>
                </div>
              </>
            )}

            {selectedRepairs.length > 0 && (
              <div className="flex items-center gap-1 px-2">
                <TablePageSelectedCountIndicator selectedCount={selectedRepairs.length} totalCount={repairsData?.pages[0]?.totalCount ?? 0} />
                {canExportCsv && <TablePageHeaderActionButton icon={<DownloadCsvIcon />} label="Download CSV" onClick={handleCsvExport} />}
                <TablePageHeaderActionButton icon={<DownloadPdfIcon />} label="Download PDF" onClick={handlePdfExport} />
                {canBulkActOnSelectedRepairs && (
                  <TablePageHeaderActionButton
                    icon={<DeleteIcon className="text-default-red" />}
                    label="Delete"
                    onClick={() => setOpenDeleteConfirmation(true)}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </TablePageHeader>

      <ScrollArea type="always" viewportRef={setScrollRoot} className="relative flex flex-1 flex-col">
        <ScrollBar orientation="horizontal" />
        <AssetRepairsTable
          data={flattenedData}
          loading={(isFetchingRepairs && !isFetchingNextRepairsPage) || isLoadingPageConfig}
          notFoundDisplay={
            debouncedSearch.length > 0 ? (
              <NoRepairFoundFromSearchDisplay />
            ) : filters && filters.length > 0 ? (
              <NoRepairFoundFromFiltersDisplay removeColumnFilters={() => setModuleFilters(MODULE.AssetRepairs, [])} />
            ) : (
              <NoRepairAvailableDisplay />
            )
          }
          rowSelection={selectedRows}
          onRowSelectionChange={setSelectedRows}
          columnFilters={filters || []}
          onColumnFiltersChange={(filters: ColumnFiltersState) => setModuleFilters(MODULE.AssetRepairs, filters)}
          initialRepairId={state?.repairId}
          sorting={sorting}
          grouping={grouping}
          columnOrder={columnOrder}
          columnVisibility={columnVisibility}
          onSortingChange={(newSorting) =>
            updatePageConfig({
              table: { sorting: newSorting },
            })
          }
        />
        {hasNextRepairsPage && (
          <Button ref={sentinelRef} onClick={loadMore} disabled={!canLoadMore} className="mx-auto w-full text-center" variant="ghost">
            {isFetchingNextRepairsPage ? 'Loading more...' : 'Load Newer'}
          </Button>
        )}
      </ScrollArea>

      <DeleteConfirmationDialog
        open={openDeleteConfirmation}
        onOpenChange={setOpenDeleteConfirmation}
        callback={handleDeleteRepairs}
        loading={isDeletingRepairs}
        title="Delete repairs?"
        description="This action is permanent and cannot be undone. Are you sure you want to delete these repairs?"
      />
    </div>
  );
};

export default AssetRepairsPage;
